/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Button, Label, Sidebar, Text, theme } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import { ServiceCommitment, serviceInitialState } from "~/models";
import { formatServiceName, useAxios } from "~/tools";
import { ServicePreview } from "../Services/ServiceAndGroupAssign/ServicePreview";
import { ServiceItem } from "../Services/types";
import { SelectedService } from "./types";

type SelectServiceProps = {
  setSelectedService: React.Dispatch<React.SetStateAction<SelectedService[]>>;
  selectedServicesIds: Array<string>;
};

function SelectService({
  setSelectedService,
  selectedServicesIds,
}: SelectServiceProps) {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);

  const [services, setServices] = React.useState([]);
  const [serviceId, setServiceId] = React.useState("");
  const [serviceDetails, setServiceDetails] = React.useState<ServiceCommitment>(
    serviceInitialState
  );

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const fetchServices = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/search/services`);
      const results = data?.searchResults.filter((service: SelectedService) => {
        return !selectedServicesIds.find((id) => id === service.id);
      });
      setServices(results);
    } catch (e) {
      showError(e.response);
    }
  }, [Axios, selectedServicesIds, showError]);

  React.useEffect(() => {
    isSidebarOpen && fetchServices();
  }, [fetchServices, isSidebarOpen]);

  const fetchService = React.useCallback(async () => {
    try {
      const { data } = await Axios().get(`/catalog/services/${serviceId}`);
      setServiceDetails(data);
    } catch (e) {
      showError(e.response);
    }
  }, [Axios, serviceId, showError]);

  React.useEffect(() => {
    serviceId.length !== 0 && fetchService();
  }, [fetchService, serviceId.length]);

  const clean = () => {
    setServiceDetails(serviceInitialState);
    setServiceId("");
  };

  const handleAdd = () => {
    setIsSidebarOpen(false);
    const service = {
      id: serviceDetails?.id,
      name: serviceDetails?.name,
      version: serviceDetails?.version,
    };

    setSelectedService((prevState: SelectedService[]) => [
      ...prevState,
      service,
    ]);
    clean();
  };

  const getValue = () =>
    services
      ?.filter((element: ServiceItem) => element?.id === serviceId)
      .map(({ id, name }) => ({
        value: id,
        label: name,
      })) || [];

  const servicesOptions =
    services?.map((serviceItem: ServiceItem) => ({
      value: serviceItem.id,
      label: formatServiceName(serviceItem),
    })) || [];

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        icon="plus"
        severity="medium"
        type="button"
      >
        Select Service
      </Button>
      <Sidebar
        title="Select Service"
        isOpen={isSidebarOpen}
        onClick={() => {
          setIsSidebarOpen(!isSidebarOpen);
          clean();
        }}
      >
        <Fieldset>
          <Label name="Service" required />
          <ServicePreview
            getValue={getValue}
            filteredServices={servicesOptions}
            setSelectedId={setServiceId}
          />
        </Fieldset>
        {serviceDetails?.description && (
          <>
            <Text tag="div" color={theme.color.text.text02}>
              Info:
            </Text>
            <Text weight="medium">{serviceDetails?.description}</Text>
          </>
        )}

        <Button
          type="submit"
          onClick={handleAdd}
          disabled={serviceId.length === 0}
          css={{ margin: "1rem auto 0 0" }}
        >
          Add
        </Button>
      </Sidebar>
    </>
  );
}

export { SelectService };
